import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Dashboard.css';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Custom collision detection that prioritizes cards over end zone
function customCollisionDetection(args) {
  const intersections = rectIntersection(args);

  // If we have multiple intersections, prioritize cards over the end zone
  if (intersections.length > 1) {
    const cardIntersections = intersections.filter(i => i.id !== 'end-drop-zone');
    if (cardIntersections.length > 0) {
      return cardIntersections;
    }
  }

  return intersections;
}

// Drop zone at the end of the list
function EndDropZone({ isOver, activeBuildName, ghostRef }) {
  const { setNodeRef } = useDroppable({
    id: 'end-drop-zone',
  });

  return (
    <div ref={setNodeRef} className={`end-drop-zone ${isOver ? 'is-over' : ''}`}>
      {isOver && (
        <div className="drop-ghost" ref={ghostRef}>
          <div className="drop-ghost-inner">
            <h3>{activeBuildName}</h3>
          </div>
        </div>
      )}
    </div>
  );
}

// Sortable build card wrapper component
function SortableBuildCard({ build, children, showGhostAtSource }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id: build.id });

  // Don't apply any transforms - we handle ghost placement manually
  const style = {
    cursor: 'grab',
  };

  // When dragging, show either ghost (if staying in place) or greyed out card
  if (isDragging) {
    if (showGhostAtSource) {
      return (
        <div ref={setNodeRef} style={style} className="drop-ghost">
          <div className="drop-ghost-inner">
            <h3>{build.name}</h3>
          </div>
        </div>
      );
    }
    return (
      <div ref={setNodeRef} style={style} className="drag-source-ghost">
        {children}
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

// Simple card preview for drag overlay
function DragPreview({ build, mousePos }) {
  const style = {
    position: 'fixed',
    left: mousePos.x,
    top: mousePos.y,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    zIndex: 9999,
  };

  return (
    <div className="drag-preview" style={style}>
      <div className="drag-preview-card">
        <h3>{build.name}</h3>
        <p>{build.projectName}</p>
      </div>
    </div>
  );
}

const Dashboard = ({ teamcityApi, buildConfigs, showPersonalBuilds, enableDebugging, onReorder, watchedBuilds, setWatchedBuilds }) => {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'before' or 'after'
  const lastPointerX = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const lastOverChange = useRef(0);
  const ghostRef = useRef(null);
  const previousBuildsRef = useRef({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    })
  );

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }
    if (Notification.permission === 'granted') {
      return true;
    }
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  };

  // Toggle watching a build
  const toggleWatchBuild = async (buildId, buildName) => {
    if (watchedBuilds[buildId]) {
      // Stop watching
      setWatchedBuilds(prev => {
        const next = { ...prev };
        delete next[buildId];
        return next;
      });
    } else {
      // Start watching - request permission first
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        setWatchedBuilds(prev => ({
          ...prev,
          [buildId]: { name: buildName, watchedAt: Date.now() }
        }));
      }
    }
  };

  // Send browser notification
  const sendNotification = (title, body, url) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: `${title}-${Date.now()}`,
      });
      notification.onclick = () => {
        window.focus();
        if (url) window.open(url, '_blank');
        notification.close();
      };
    }
  };

  // Check for build completions and send notifications
  // Uses the actual build instance ID (latestBuild.id) to match with BuildServers and dependencies
  useEffect(() => {
    if (builds.length === 0) return;

    // Collect all current build IDs (main builds and dependencies)
    const currentBuildIds = new Set();
    builds.forEach(build => {
      if (build.latestBuild?.id) {
        currentBuildIds.add(build.latestBuild.id);
        currentBuildIds.add(String(build.latestBuild.id));
      }
      if (build.dependencies) {
        build.dependencies.forEach(dep => {
          if (dep.latestBuild?.id) {
            currentBuildIds.add(dep.latestBuild.id);
            currentBuildIds.add(String(dep.latestBuild.id));
          }
        });
      }
    });

    // Check watched builds that were running but are no longer present
    Object.keys(watchedBuilds).forEach(buildId => {
      // Skip test builds (handled by BuildServers)
      if (buildId.startsWith('test-build-')) return;

      const prevBuildState = previousBuildsRef.current[buildId];

      // If we were tracking this build as running and it's no longer in the list, it completed
      if (prevBuildState &&
          (prevBuildState.state === 'running' || prevBuildState.state === 'queued') &&
          !currentBuildIds.has(Number(buildId)) &&
          !currentBuildIds.has(buildId)) {
        const buildName = watchedBuilds[buildId]?.name || 'Build';
        sendNotification(
          `Build Complete: ${buildName}`,
          '✅ Completed',
          null
        );
        setWatchedBuilds(prev => {
          const next = { ...prev };
          delete next[buildId];
          return next;
        });
        // Clean up previous state
        delete previousBuildsRef.current[buildId];
      }
    });

    // Helper to check and notify for a build
    const checkBuildCompletion = (buildInstanceId, buildName, currentState, currentStatus, statusText, webUrl) => {
      if (!buildInstanceId) return;

      const prevBuildState = previousBuildsRef.current[buildInstanceId];

      // Only notify for watched builds
      if (!watchedBuilds[buildInstanceId]) {
        // Still track state for builds that might be watched later
        previousBuildsRef.current[buildInstanceId] = {
          state: currentState,
          status: currentStatus,
          name: buildName,
        };
        return;
      }

      // Check if build just completed (was running/queued, now finished)
      if (prevBuildState) {
        const wasInProgress = prevBuildState.state === 'running' || prevBuildState.state === 'queued';
        const isNowFinished = currentState === 'finished';

        if (wasInProgress && isNowFinished) {
          const status = currentStatus === 'SUCCESS' ? '✅ Success' : '❌ Failed';
          sendNotification(
            `Build Complete: ${buildName}`,
            `${status} - ${statusText || currentStatus}`,
            webUrl
          );
          // Auto-remove from watched after notification
          setWatchedBuilds(prev => {
            const next = { ...prev };
            delete next[buildInstanceId];
            return next;
          });
        }
      }

      // Store current state for next comparison
      previousBuildsRef.current[buildInstanceId] = {
        state: currentState,
        status: currentStatus,
        name: buildName,
      };
    };

    // Check main builds
    builds.forEach(build => {
      const currentBuild = build.latestBuild;
      if (currentBuild) {
        checkBuildCompletion(
          currentBuild.id,
          build.name,
          currentBuild.state,
          currentBuild.status,
          currentBuild.statusText,
          currentBuild.webUrl
        );
      }

      // Also check dependencies
      if (build.dependencies) {
        build.dependencies.forEach(dep => {
          if (dep.latestBuild) {
            checkBuildCompletion(
              dep.latestBuild.id,
              dep.name,
              dep.latestBuild.state,
              dep.latestBuild.status,
              dep.latestBuild.statusText,
              dep.latestBuild.webUrl
            );
          }
        });
      }
    });
  }, [builds, watchedBuilds, setWatchedBuilds]);

  // Track mouse position globally while dragging
  useEffect(() => {
    if (!activeId) return;

    function handleMouseMove(e) {
      setMousePos({ x: e.clientX, y: e.clientY });
    }

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [activeId]);

  function handleDragStart(event) {
    setActiveId(event.active.id);
    if (event.activatorEvent) {
      setMousePos({ x: event.activatorEvent.clientX, y: event.activatorEvent.clientY });
    }
  }

  function isMouseOverGhost() {
    if (!ghostRef.current) return false;
    const rect = ghostRef.current.getBoundingClientRect();
    return mousePos.x >= rect.left && mousePos.x <= rect.right &&
           mousePos.y >= rect.top && mousePos.y <= rect.bottom;
  }

  function handleDragMove(event) {
    const { over, active, activatorEvent } = event;

    // If dragging over the active card itself (the ghost/source position), do nothing
    if (over && over.id === active.id) {
      return;
    }

    // If mouse is over the drop ghost, don't recalculate
    if (isMouseOverGhost()) {
      return;
    }

    // Debounce all overId changes to prevent flickering
    const now = Date.now();

    if (!over) {
      // When not over anything specific, default to end-drop-zone
      if (overId !== 'end-drop-zone') {
        if (now - lastOverChange.current < 50) return;
        lastOverChange.current = now;
        setOverId('end-drop-zone');
      }
      if (dropPosition !== 'after') setDropPosition('after');
      return;
    }

    // Handle end drop zone separately
    if (over.id === 'end-drop-zone') {
      if (overId !== over.id) {
        if (now - lastOverChange.current < 50) return;
        lastOverChange.current = now;
        setOverId(over.id);
      }
      if (dropPosition !== 'after') setDropPosition('after');
      return;
    }

    // Debounce card changes
    const isNewCard = overId !== over.id;
    if (isNewCard) {
      if (now - lastOverChange.current < 50) {
        // Too soon, ignore this change
        return;
      }
      lastOverChange.current = now;
      setOverId(over.id);
    }

    // Use actual mouse position for drop calculation
    const pointerX = mousePos.x;

    // Get the over element's rect
    const overRect = over.rect;

    if (overRect && pointerX !== undefined) {
      const cardMidpoint = overRect.left + overRect.width / 2;

      // When entering a new card, use simple 50% threshold
      // Only apply hysteresis when staying on the same card
      let newDropPosition;
      if (isNewCard) {
        newDropPosition = pointerX > cardMidpoint ? 'after' : 'before';
      } else {
        // Add hysteresis - require 20% movement past midpoint to switch
        const hysteresis = overRect.width * 0.20;
        newDropPosition = dropPosition;

        if (dropPosition === 'before' && pointerX > cardMidpoint + hysteresis) {
          newDropPosition = 'after';
        } else if (dropPosition === 'after' && pointerX < cardMidpoint - hysteresis) {
          newDropPosition = 'before';
        } else if (dropPosition === null) {
          newDropPosition = pointerX > cardMidpoint ? 'after' : 'before';
        }
      }

      if (dropPosition !== newDropPosition) setDropPosition(newDropPosition);
      lastPointerX.current = pointerX;
    }
  }

  function handleDragOver(event) {
    // Also handle dragOver for cases where dragMove might not fire
    const { over, active, activatorEvent } = event;

    // If dragging over the active card itself (the ghost/source position), do nothing
    if (over && over.id === active.id) {
      return;
    }

    // If mouse is over the drop ghost, don't recalculate
    if (isMouseOverGhost()) {
      return;
    }

    // Debounce all overId changes to prevent flickering
    const now = Date.now();

    if (!over) {
      // When not over anything specific, default to end-drop-zone
      if (overId !== 'end-drop-zone') {
        if (now - lastOverChange.current < 50) return;
        lastOverChange.current = now;
        setOverId('end-drop-zone');
      }
      if (dropPosition !== 'after') setDropPosition('after');
      return;
    }

    // Handle end drop zone separately
    if (over.id === 'end-drop-zone') {
      if (overId !== over.id) {
        if (now - lastOverChange.current < 50) return;
        lastOverChange.current = now;
        setOverId(over.id);
      }
      if (dropPosition !== 'after') setDropPosition('after');
      return;
    }

    // Debounce card changes
    const isNewCard = overId !== over.id;
    if (isNewCard) {
      if (now - lastOverChange.current < 50) {
        // Too soon, ignore this change
        return;
      }
      lastOverChange.current = now;
      setOverId(over.id);
    }

    // Use actual mouse position for drop calculation
    const pointerX = mousePos.x;
    const overRect = over.rect;

    if (overRect && pointerX !== undefined) {
      const cardMidpoint = overRect.left + overRect.width / 2;

      // When entering a new card, use simple 50% threshold
      // Only apply hysteresis when staying on the same card
      let newDropPosition;
      if (isNewCard) {
        newDropPosition = pointerX > cardMidpoint ? 'after' : 'before';
      } else {
        // Add hysteresis - require 20% movement past midpoint to switch
        const hysteresis = overRect.width * 0.20;
        newDropPosition = dropPosition;

        if (dropPosition === 'before' && pointerX > cardMidpoint + hysteresis) {
          newDropPosition = 'after';
        } else if (dropPosition === 'after' && pointerX < cardMidpoint - hysteresis) {
          newDropPosition = 'before';
        } else if (dropPosition === null) {
          newDropPosition = pointerX > cardMidpoint ? 'after' : 'before';
        }
      }

      if (dropPosition !== newDropPosition) setDropPosition(newDropPosition);
      lastPointerX.current = pointerX;
    }
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over) {
      const oldIndex = builds.findIndex((b) => b.id === active.id);

      // Handle dropping on end zone
      if (over.id === 'end-drop-zone') {
        if (oldIndex !== -1 && oldIndex !== builds.length - 1) {
          const newBuilds = arrayMove(builds, oldIndex, builds.length - 1);
          setBuilds(newBuilds);

          if (onReorder) {
            const newOrder = newBuilds.map(b => ({
              id: b.id,
              name: b.name,
              projectName: b.projectName
            }));
            onReorder(newOrder);
          }
        }
      } else if (active.id !== over.id && dropPosition) {
        const overIndex = builds.findIndex((b) => b.id === over.id);

        if (oldIndex !== -1 && overIndex !== -1) {
          // Calculate new index based on drop position
          let newIndex = overIndex;

          // Adjust index based on whether we're dropping before or after
          if (dropPosition === 'after' && oldIndex < overIndex) {
            // Dragging right, dropping after: arrayMove handles this correctly
            newIndex = overIndex;
          } else if (dropPosition === 'before' && oldIndex > overIndex) {
            // Dragging left, dropping before: arrayMove handles this correctly
            newIndex = overIndex;
          } else if (dropPosition === 'after' && oldIndex > overIndex) {
            // Dragging left but want to drop after: need to add 1
            newIndex = overIndex + 1;
          } else if (dropPosition === 'before' && oldIndex < overIndex) {
            // Dragging right but want to drop before: need to subtract 1
            newIndex = overIndex - 1;
          }

          const newBuilds = arrayMove(builds, oldIndex, newIndex);
          setBuilds(newBuilds);

          // Notify parent component of the new order
          if (onReorder) {
            const newOrder = newBuilds.map(b => ({
              id: b.id,
              name: b.name,
              projectName: b.projectName
            }));
            onReorder(newOrder);
          }
        }
      }
    }

    setActiveId(null);
    setOverId(null);
    setDropPosition(null);
  }

  const fetchBuilds = useCallback(async () => {
    if (!teamcityApi || buildConfigs.length === 0) {
      setBuilds([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const buildIds = buildConfigs.map(config => config.id);
      const buildsData = await teamcityApi.getBuildsInfo(buildIds);

      // Sort builds according to buildConfigs order
      const sortedBuilds = buildIds.map(id =>
        buildsData.find(build => build.id === id)
      ).filter(Boolean);

      // Filter out personal builds if showPersonalBuilds is false
      const filteredBuilds = showPersonalBuilds
        ? sortedBuilds
        : sortedBuilds.map(build => ({
            ...build,
            latestBuild: build.latestBuild?.personal ? null : build.latestBuild,
            previousBuild: build.previousBuild?.personal ? null : build.previousBuild,
          }));

      setBuilds(filteredBuilds);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamcityApi, buildConfigs, showPersonalBuilds]);

  useEffect(() => {
    fetchBuilds();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchBuilds, 30000);
    return () => clearInterval(interval);
  }, [fetchBuilds]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatShortDateTime = (dateString) => {
    if (!dateString) return '';

    // Handle TeamCity format: 20251120T141448+0000
    let date;
    if (dateString.match(/^\d{8}T\d{6}/)) {
      // Parse format like 20251120T141448+0000
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      const hours = dateString.substring(9, 11);
      const minutes = dateString.substring(11, 13);
      const seconds = dateString.substring(13, 15);
      date = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    return `${month}/${day} ${hours}:${minutes}${ampm}`;
  };

  const getCustomBuildNumber = (build) => {
    if (!build || !build.properties) {
      return build?.number || 'N/A';
    }

    const vcsNumber = build.properties['build.vcs.number.client'];
    const buildNumber = build.properties['build.number'] || build.number;

    if (vcsNumber && buildNumber) {
      return `${vcsNumber} (${buildNumber})`;
    }

    // If we have VCS number but no build number, show just VCS number
    if (vcsNumber) {
      return vcsNumber;
    }

    return build.number || 'N/A';
  };

  const formatTimeRemaining = (build) => {
    if (build && build['running-info']) {
      const runningInfo = build['running-info'];
      const percentageComplete = runningInfo.percentageComplete;
      const estimatedTotalSeconds = runningInfo.estimatedTotalSeconds;

      if (estimatedTotalSeconds && percentageComplete !== undefined) {
        // Calculate remaining time based on percentage and total estimated time
        const remainingSeconds = Math.max(0, Math.round((100 - percentageComplete) / 100 * estimatedTotalSeconds));

        if (remainingSeconds === 0) {
          return '0m';
        }

        const hours = Math.floor(remainingSeconds / 3600);
        const minutes = Math.floor((remainingSeconds % 3600) / 60);

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
          return `${minutes}m`;
        } else {
          return '1m';
        }
      }
    }
    return null;
  };

  const formatTimeElapsed = (build) => {
    if (build && build['running-info']) {
      const runningInfo = build['running-info'];
      const elapsedSeconds = runningInfo.elapsedSeconds;

      if (elapsedSeconds !== undefined) {
        const hours = Math.floor(elapsedSeconds / 3600);
        const minutes = Math.floor((elapsedSeconds % 3600) / 60);

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
          return `${minutes}m`;
        } else {
          return '<1m';
        }
      }
    }
    return null;
  };

  const getPlatformName = (fullName) => {
    // Look for platform keywords in the name
    const platforms = ['iOS', 'Android', 'Windows', 'Mac'];
    for (const platform of platforms) {
      if (fullName.match(new RegExp(platform, 'i'))) {
        return platform;
      }
    }
    // If no platform found, return the full name
    return fullName;
  };

  const getPlatformIcon = (platformName) => {
    switch (platformName.toLowerCase()) {
      case 'android':
        return '🤖';
      case 'ios':
        return '🍎';
      case 'windows':
        return '🪟';
      case 'mac':
        return '🖥️';
      default:
        return null;
    }
  };

  const getSuccessfulDependencyIcons = (dependencies) => {
    if (!dependencies || dependencies.length === 0) return [];
    return dependencies
      .filter(dep => dep.latestBuild?.status === 'SUCCESS')
      .map(dep => ({
        platform: getPlatformName(dep.name),
        icon: getPlatformIcon(getPlatformName(dep.name)),
        webUrl: dep.latestBuild?.webUrl
      }))
      .filter(item => item.icon !== null);
  };

  const formatBuildName = (name) => {
    if (!name) return name;
    const keywords = ['next_dev', 'current_dev', 'production', 'performance'];
    let result = name;

    // Find and wrap keywords with bold
    for (const keyword of keywords) {
      const regex = new RegExp(`(${keyword})`, 'gi');
      result = result.replace(regex, '<strong>$1</strong>');
    }

    return result;
  };

  const getStatusColor = (build) => {
    if (build.error) return 'error';
    if (!build.latestBuild) return 'no-builds';

    // When there's an in-progress build, use the previous build's status for the card color
    if (build.previousBuild && (build.latestBuild.state === 'queued' || build.latestBuild.state === 'running')) {
      const prevStatus = build.previousBuild.status;
      if (prevStatus === 'FAILURE') {
        return 'failure';
      } else if (prevStatus === 'SUCCESS') {
        return 'success';
      }
      return 'success';
    }

    const state = build.latestBuild.state;
    const status = build.latestBuild.status;

    if (state === 'queued') {
      return 'queued';
    } else if (state === 'running') {
      return 'running';
    } else if (status === 'FAILURE') {
      return 'failure';
    } else if (status === 'SUCCESS') {
      return 'success';
    }

    return 'success';
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Builds</h2>
        <div className="dashboard-actions">
          {lastRefresh && (
            <span className="last-refresh">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          Error fetching builds: {error}
        </div>
      )}

      {buildConfigs.length === 0 ? (
        <div className="empty-state">
          <p>No build configurations found.</p>
          <p>Add build configuration IDs to <code>REACT_APP_DEFAULT_BUILD_CONFIGS</code> in your <code>.env</code> file.</p>
          <p>Example: <code>REACT_APP_DEFAULT_BUILD_CONFIGS=MyProject_Build,AnotherProject_Deploy</code></p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={builds.map(b => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={`builds-grid ${activeId ? 'is-dragging' : ''}`}>
              {builds.map((build, index) => {
                const activeBuild = builds.find(b => b.id === activeId);
                const activeIndex = builds.findIndex(b => b.id === activeId);
                const overIndex = builds.findIndex(b => b.id === overId);
                const isOver = overId === build.id && activeId !== build.id;
                const isActiveCard = build.id === activeId;

                // Check if the drop would result in no change
                const wouldDropBefore = isOver && dropPosition === 'before';
                const wouldDropAfter = isOver && dropPosition === 'after';

                // Card would stay in same position if:
                // - hovering on next card's left half (drop before next = stay)
                // - hovering on previous card's right half (drop after prev = stay)
                const wouldStaySame = (wouldDropBefore && activeIndex === index - 1) ||
                                      (wouldDropAfter && activeIndex === index + 1);

                const showGhostBefore = wouldDropBefore && !wouldStaySame;
                const showGhostAfter = wouldDropAfter && !wouldStaySame;

                // Show ghost at source position when card would stay in place
                // This happens when:
                // 1. Hovering over the card itself (overId === activeId or no overId)
                // 2. Hovering on adjacent card in a way that wouldn't move it
                // 3. First card hovering over end-drop-zone (fallback, not intentional move)
                // 4. Last card hovering over end-drop-zone (would stay in place)
                const hoveringOverSelf = isActiveCard && (overId === activeId || !overId);
                const dragWouldStaySame = (dropPosition === 'before' && activeIndex === overIndex - 1) ||
                                          (dropPosition === 'after' && activeIndex === overIndex + 1);
                const isFirstCard = activeIndex === 0;
                const isLastCard = activeIndex === builds.length - 1;
                const endZoneFallback = isActiveCard && overId === 'end-drop-zone' && (isFirstCard || isLastCard);
                const showGhostAtSource = isActiveCard && (hoveringOverSelf || dragWouldStaySame || endZoneFallback);

                return (
                  <React.Fragment key={build.id || index}>
                    {showGhostBefore && (
                      <div className="drop-ghost" ref={ghostRef}>
                        <div className="drop-ghost-inner">
                          <h3>{activeBuild?.name}</h3>
                        </div>
                      </div>
                    )}
                    <SortableBuildCard
                      build={build}
                      showGhostAtSource={showGhostAtSource}
                    >
                    <div className="build-card-container">
              {/* Main build card */}
              <div className={`build-card ${getStatusColor(build)}`}>
                <div className="build-card-header">
                  <div>
                    <h3 dangerouslySetInnerHTML={{ __html: formatBuildName(build.name) }} />
                    <p className="project-name">{build.projectName}</p>
                  </div>
                  <div className="build-header-right">
                    {/* Platform icons for successful dependencies of the displayed build */}
                    {(() => {
                      // When showing previousBuild (in-progress scenario), use previousBuild's dependencies
                      // Otherwise use the latestBuild's dependencies
                      const isShowingPreviousBuild = build.previousBuild && (build.latestBuild?.state === 'queued' || build.latestBuild?.state === 'running');
                      const deps = isShowingPreviousBuild
                        ? build.previousBuildDependencies
                        : build.dependencies;
                      const successIcons = getSuccessfulDependencyIcons(deps);
                      return successIcons.length > 0 && (
                        <div className="platform-icons">
                          {successIcons.map((item, idx) => (
                            <a
                              key={idx}
                              href={item.webUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="platform-icon-link"
                              title={`${item.platform} build`}
                            >
                              <span className="platform-icon">{item.icon}</span>
                            </a>
                          ))}
                        </div>
                      );
                    })()}
                    {(build.previousBuild || build.latestBuild)?.webUrl && (
                      <div className="build-header-icons">
                        <a
                          href={(build.previousBuild && (build.latestBuild?.state === 'queued' || build.latestBuild?.state === 'running') ? build.previousBuild : build.latestBuild)?.webUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="api-icon-link"
                          title="View in TeamCity"
                        >
                          <span className="teamcity-icon">🔗</span>
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="build-card-body">
                  {build.error ? (
                    <div className="build-error">
                      <p>Error: {build.error}</p>
                    </div>
                  ) : build.previousBuild && (build.latestBuild?.state === 'queued' || build.latestBuild?.state === 'running') ? (
                    /* Show previous build when there's an in-progress build */
                    <div className="build-info">
                      <div className="build-number">
                        <span className="label">Build Number:</span>
                        <span className="value">{getCustomBuildNumber(build.previousBuild)}</span>
                      </div>
                      <div className="build-status">
                        <span className="label">Status:</span>
                        <span className="build-status-content">
                          <span className="build-datetime">
                            {formatShortDateTime(build.previousBuild.finishDate)}
                          </span>
                          <span className={`value ${build.previousBuild.status ? `status-${build.previousBuild.status.toLowerCase()}` : ''}`}>
                            {build.previousBuild.status || 'Unknown'}
                          </span>
                        </span>
                      </div>
                      {build.previousBuild.statusText && (
                        <div className="build-status-text">
                          <span className="label">Details:</span>
                          <span className="value">{build.previousBuild.statusText}</span>
                        </div>
                      )}
                    </div>
                  ) : build.latestBuild ? (
                  /* Normal display when no in-progress build */
                  <div className="build-info">
                    <div className="build-number">
                      <span className="label">Build Number:</span>
                      <span className="value">{getCustomBuildNumber(build.latestBuild)}</span>
                    </div>
                    <div className="build-status">
                      <span className="label">Status:</span>
                      <span className="build-status-content">
                        <span className="build-datetime">
                          {formatShortDateTime(
                            build.latestBuild.state === 'queued' ? build.latestBuild.queuedDate :
                            build.latestBuild.state === 'running' ? build.latestBuild.startDate :
                            build.latestBuild.finishDate
                          )}
                        </span>
                        <span className={`value ${build.latestBuild.state === 'queued' ? 'status-queued' : build.latestBuild.state === 'running' ? 'status-running' : build.latestBuild.status ? `status-${build.latestBuild.status.toLowerCase()}` : ''}`}>
                          {(() => {
                            if (build.latestBuild.state === 'queued') {
                              const hasRunningDeps = build.dependencies?.some(dep => dep.latestBuild?.state === 'running');
                              return hasRunningDeps ? 'Building Dependencies' : 'QUEUED';
                            } else if (build.latestBuild.state === 'running') {
                              return 'RUNNING';
                            } else {
                              return build.latestBuild.status || 'Unknown';
                            }
                          })()}
                        </span>
                      </span>
                    </div>
                    {build.latestBuild.statusText && (
                      <div className="build-status-text">
                        <span className="label">Details:</span>
                        <span className="value">{build.latestBuild.statusText}</span>
                      </div>
                    )}

                    {/* Show progress bar for running builds */}
                    {build.latestBuild.state === 'running' && build.latestBuild.percentageComplete !== undefined && build.latestBuild.percentageComplete >= 0 && (
                      <div className="build-progress">
                        <div className="build-progress-bar">
                          <div
                            className={`build-progress-fill ${build.latestBuild.status === 'FAILURE' ? 'build-progress-fill-failure' : ''}`}
                            style={{ width: `${Math.min(build.latestBuild.percentageComplete, 100)}%` }}
                          />
                          <span className="build-time-elapsed-overlay">{formatTimeElapsed(build.latestBuild)}</span>
                          <span className="build-progress-text">{build.name}</span>
                          <div className={`build-progress-icons ${watchedBuilds[build.latestBuild.id] ? 'has-watching' : ''}`}>
                            {build.latestBuild?.webUrl && (
                              <a
                                href={build.latestBuild.webUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="api-icon-link"
                                title="View in TeamCity"
                              >
                                <span className="teamcity-icon">🔗</span>
                              </a>
                            )}
                            <button
                              className={`notification-bell ${watchedBuilds[build.latestBuild.id] ? 'watching' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleWatchBuild(build.latestBuild.id, build.name);
                              }}
                              title={watchedBuilds[build.latestBuild.id] ? 'Stop watching this build' : 'Notify me when this build completes'}
                            >
                              {watchedBuilds[build.latestBuild.id] ? '🔔' : '🔕'}
                            </button>
                          </div>
                          <span className="build-time-remaining-overlay">{formatTimeRemaining(build.latestBuild)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-builds">
                    <p>No builds found</p>
                  </div>
                )}
              </div>
            </div>

              {/* In-progress build card (separate card underneath) */}
              {build.previousBuild && (build.latestBuild?.state === 'queued' || build.latestBuild?.state === 'running') && (
                <div className={`build-card in-progress-card ${build.latestBuild.state === 'running' ? 'running' : 'queued'}`}>
                  <div className="build-card-header">
                    <div>
                      <h3 dangerouslySetInnerHTML={{ __html: formatBuildName(build.name) }} />
                      <p className="project-name">{build.projectName}</p>
                    </div>
                    <div className="build-header-right">
                      {build.latestBuild?.webUrl && (
                        <div className="build-header-icons">
                          <a
                            href={build.latestBuild.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="api-icon-link"
                            title="View in TeamCity"
                          >
                            <span className="teamcity-icon">🔗</span>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="build-card-body">
                    <div className="build-info">
                      <div className="build-number">
                        <span className="label">Build Number:</span>
                        <span className="value">{getCustomBuildNumber(build.latestBuild)}</span>
                      </div>
                      <div className="build-status">
                        <span className="label">Status:</span>
                        <span className="build-status-content">
                          <span className="build-datetime">
                            {formatShortDateTime(
                              build.latestBuild.state === 'queued' ? build.latestBuild.queuedDate : build.latestBuild.startDate
                            )}
                          </span>
                          <span className={`value ${build.latestBuild.state === 'queued' ? 'status-queued' : 'status-running'}`}>
                            {build.latestBuild.state === 'queued' ? (
                              (() => {
                                const hasRunningDeps = build.dependencies?.some(dep => dep.latestBuild?.state === 'running');
                                return hasRunningDeps ? 'BUILDING DEPENDENCIES' : 'QUEUED';
                              })()
                            ) : 'RUNNING'}
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Show progress bar for running builds */}
                    {build.latestBuild.state === 'running' && build.latestBuild.percentageComplete !== undefined && build.latestBuild.percentageComplete >= 0 && (
                      <div className="build-progress">
                        <div className="build-progress-bar">
                          <div
                            className={`build-progress-fill ${build.latestBuild.status === 'FAILURE' ? 'build-progress-fill-failure' : ''}`}
                            style={{ width: `${Math.min(build.latestBuild.percentageComplete, 100)}%` }}
                          />
                          <span className="build-time-elapsed-overlay">{formatTimeElapsed(build.latestBuild)}</span>
                          <span className="build-progress-text">{build.name}</span>
                          <div className={`build-progress-icons ${watchedBuilds[build.latestBuild.id] ? 'has-watching' : ''}`}>
                            {build.latestBuild?.webUrl && (
                              <a
                                href={build.latestBuild.webUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="api-icon-link"
                                title="View in TeamCity"
                              >
                                <span className="teamcity-icon">🔗</span>
                              </a>
                            )}
                            <button
                              className={`notification-bell ${watchedBuilds[build.latestBuild.id] ? 'watching' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleWatchBuild(build.latestBuild.id, build.name);
                              }}
                              title={watchedBuilds[build.latestBuild.id] ? 'Stop watching this build' : 'Notify me when this build completes'}
                            >
                              {watchedBuilds[build.latestBuild.id] ? '🔔' : '🔕'}
                            </button>
                          </div>
                          <span className="build-time-remaining-overlay">{formatTimeRemaining(build.latestBuild)}</span>
                        </div>
                      </div>
                    )}

                    {/* Dependencies */}
                    {build.dependencies && build.dependencies.length > 0 && (
                      <div className="build-dependencies">
                      <div className="label">Dependencies:</div>
                      <div className="dependencies-list">
                        {build.dependencies.map((dep, idx) => {
                          const buildXmlUrl = dep.latestBuild?.href
                            ? `https://azragames.teamcity.com${dep.latestBuild.href}`
                            : null;
                          const propertiesUrl = dep.latestBuild?.href
                            ? `https://azragames.teamcity.com${dep.latestBuild.href}/resulting-properties`
                            : null;

                          const isRunning = dep.latestBuild?.state === 'running' &&
                                           dep.latestBuild?.percentageComplete !== undefined &&
                                           dep.latestBuild?.percentageComplete >= 0;

                          const platformName = getPlatformName(dep.name);
                          const statusText = dep.latestBuild?.statusText || '';

                          return (
                            <div key={dep.id || idx} className="dependency-item">
                              {isRunning ? (
                                <div className="dependency-progress-bar">
                                  <div
                                    className={`dependency-progress-fill ${dep.latestBuild.status === 'FAILURE' ? 'dependency-progress-fill-failure' : ''}`}
                                    style={{ width: `${Math.min(dep.latestBuild.percentageComplete, 100)}%` }}
                                  />
                                  <span className="dependency-time-elapsed-overlay">
                                    {formatTimeElapsed(dep.latestBuild)}
                                  </span>
                                  <a
                                    href={dep.latestBuild.webUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="dependency-name-link-progress"
                                  >
                                    <span className="dependency-name-progress">
                                      {platformName}
                                      {statusText && <span className="dependency-stage"> {statusText}</span>}
                                    </span>
                                  </a>
                                  <span className="dependency-time-remaining-overlay">
                                    {formatTimeRemaining(dep.latestBuild)}
                                  </span>
                                  <div className={`dependency-progress-icons ${watchedBuilds[dep.latestBuild.id] ? 'has-watching' : ''}`}>
                                    {dep.latestBuild?.webUrl && (
                                      <a
                                        href={dep.latestBuild.webUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="api-icon-link"
                                        title="View in TeamCity"
                                      >
                                        <span className="teamcity-icon">🔗</span>
                                      </a>
                                    )}
                                    {enableDebugging && buildXmlUrl && (
                                      <a
                                        href={buildXmlUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="api-icon-link"
                                        title="View build XML"
                                      >
                                        <span className="xml-icon">⚡</span>
                                      </a>
                                    )}
                                    {enableDebugging && propertiesUrl && (
                                      <a
                                        href={propertiesUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="api-icon-link"
                                        title="View resolved properties"
                                      >
                                        <span className="properties-icon">🔧</span>
                                      </a>
                                    )}
                                    <button
                                      className={`notification-bell ${watchedBuilds[dep.latestBuild.id] ? 'watching' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleWatchBuild(dep.latestBuild.id, dep.name);
                                      }}
                                      title={watchedBuilds[dep.latestBuild.id] ? 'Stop watching this build' : 'Notify me when this build completes'}
                                    >
                                      {watchedBuilds[dep.latestBuild.id] ? '🔔' : '🔕'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="dependency-content">
                                    {dep.latestBuild?.webUrl ? (
                                      <a
                                        href={dep.latestBuild.webUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="dependency-name-link"
                                      >
                                        <span className="dependency-name">{platformName}</span>
                                      </a>
                                    ) : (
                                      <span className="dependency-name">{platformName}</span>
                                    )}
                                    <div className="dependency-icons">
                                      {dep.latestBuild?.webUrl && (
                                        <a
                                          href={dep.latestBuild.webUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="api-icon-link"
                                          title="View in TeamCity"
                                        >
                                          <span className="teamcity-icon">🔗</span>
                                        </a>
                                      )}
                                      {enableDebugging && buildXmlUrl && (
                                        <a
                                          href={buildXmlUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="api-icon-link"
                                          title="View build XML"
                                        >
                                          <span className="xml-icon">⚡</span>
                                        </a>
                                      )}
                                      {enableDebugging && propertiesUrl && (
                                        <a
                                          href={propertiesUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="api-icon-link"
                                          title="View resolved properties"
                                        >
                                          <span className="properties-icon">🔧</span>
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  {dep.error ? (
                                    <span className="dependency-status error">✗</span>
                                  ) : dep.latestBuild ? (
                                    dep.latestBuild.state === 'queued' ? (
                                      <span className="dependency-status status-queued">⏳</span>
                                    ) : dep.latestBuild.status ? (
                                      <span className={`dependency-status status-${dep.latestBuild.status.toLowerCase()}`}>
                                        {dep.latestBuild.status === 'SUCCESS' ? '✓' : dep.latestBuild.status === 'FAILURE' ? '✗' : dep.latestBuild.status}
                                      </span>
                                    ) : (
                                      <span className="dependency-status no-build">—</span>
                                    )
                                  ) : (
                                    <span className="dependency-status no-build">—</span>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
                </SortableBuildCard>
                    {showGhostAfter && (
                      <div className="drop-ghost" ref={ghostRef}>
                        <div className="drop-ghost-inner">
                          <h3>{activeBuild?.name}</h3>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {activeId && (() => {
                const activeIndex = builds.findIndex(b => b.id === activeId);
                const isLastCard = activeIndex === builds.length - 1;
                const isFirstCard = activeIndex === 0;
                // Don't show end zone ghost if:
                // - dragging the last card (it would stay in place)
                // - dragging the first card (end-drop-zone is just a fallback, not intentional)
                const showEndZone = overId === 'end-drop-zone' && !isLastCard && !isFirstCard;
                return (
                  <EndDropZone
                    isOver={showEndZone}
                    activeBuildName={builds.find(b => b.id === activeId)?.name}
                    ghostRef={ghostRef}
                  />
                );
              })()}
            </div>
          </SortableContext>
          </DndContext>
      )}
      {activeId && (
        <DragPreview build={builds.find(b => b.id === activeId)} mousePos={mousePos} />
      )}
    </div>
  );
};

export default Dashboard;
